/**
 * Language allowlist and public-enablement config.
 *
 * Languages are keyed by BCP-47 codes, including regional variants (e.g.
 * `es-ES` vs `es-419`). Every enabled language has a matching translation file
 * in `resources/<code>.json` (validated by scripts/validate-i18n.js). Regional
 * variants may start as copies of their base language and diverge over time.
 */

export type SupportedLanguage =
  | 'en'
  | 'es-ES'
  | 'es-419'
  | 'pt-BR'
  | 'pt-PT'
  | 'fr-FR'
  | 'fr-CA';

export const DEFAULT_LANGUAGE: SupportedLanguage = 'en';

export type LanguageConfigEntry = {
  /** English name, for internal tooling and logs. */
  label: string;
  /** Name in the language itself, shown in the language picker. */
  nativeLabel: string;
  /** Whether the language is offered publicly. */
  enabled: boolean;
  /** Right-to-left script. Keep disabled until RTL layout is tested. */
  rtl?: boolean;
};

// Order here is the order shown in the language picker.
export const LANGUAGE_CONFIG: Record<SupportedLanguage, LanguageConfigEntry> = {
  en: {
    label: 'English',
    nativeLabel: 'English',
    enabled: true,
  },
  'es-ES': {
    label: 'Spanish (Spain)',
    nativeLabel: 'Español (España)',
    enabled: true,
  },
  'es-419': {
    label: 'Spanish (Latin America)',
    nativeLabel: 'Español (Latinoamérica)',
    enabled: true,
  },
  'pt-BR': {
    label: 'Portuguese (Brazil)',
    nativeLabel: 'Português (Brasil)',
    enabled: true,
  },
  'pt-PT': {
    label: 'Portuguese (Portugal)',
    nativeLabel: 'Português (Portugal)',
    enabled: true,
  },
  'fr-FR': {
    label: 'French (France)',
    nativeLabel: 'Français (France)',
    enabled: true,
  },
  'fr-CA': {
    label: 'French (Canada)',
    nativeLabel: 'Français (Canada)',
    enabled: true,
  },
};

export const SUPPORTED_LANGUAGES = Object.keys(
  LANGUAGE_CONFIG,
) as SupportedLanguage[];

export function isSupportedLanguage(
  language: string | null | undefined,
): language is SupportedLanguage {
  return !!language && language in LANGUAGE_CONFIG;
}

export function isLanguageEnabled(
  language: string | null | undefined,
): boolean {
  return isSupportedLanguage(language) && LANGUAGE_CONFIG[language].enabled;
}

/** Languages that may be shown in the public language picker. */
export function getEnabledLanguages(): SupportedLanguage[] {
  return SUPPORTED_LANGUAGES.filter(
    (language) => LANGUAGE_CONFIG[language].enabled,
  );
}

export function getLanguageDisplayName(language: SupportedLanguage): string {
  return LANGUAGE_CONFIG[language].nativeLabel;
}

export function isRTLLanguage(language: SupportedLanguage): boolean {
  return LANGUAGE_CONFIG[language].rtl === true;
}
